import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Undo as UndoIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
// Firestore operations will be handled via API
// Rollback will be handled via API
import { updateAgentPermissions } from '../services/api';
import { useAuth } from '../contexts/GoogleAuthContext';
import axios from 'axios';

// TODO: This is a stub - rollback functionality needs to be implemented via the proper API
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_API_URL || 'https://command-center-backend-api-55189446448.us-central1.run.app',
});

interface RollbackToken {
  created_at: string;
  expires_at: string;
  env: string;
  functionName: string;
  target_version_for_rollback: any;
  version_rolled_from: any;
  audit_log_id?: string;
}

export default function Rollback() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rollbackData, setRollbackData] = useState<RollbackToken | null>(null);
  const [error, setError] = useState<string>('');
  const [executing, setExecuting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadRollbackData();
  }, [token]);

  const loadRollbackData = async () => {
    if (!token) {
      setError('Invalid rollback link');
      setLoading(false);
      return;
    }

    try {
      // TODO: Implement API call to get rollback token
      const response = await apiClient.get(`/api/rollback-tokens/${token}`);
      const tokenDoc = { exists: () => response.data != null, data: () => response.data };

      if (!tokenDoc.exists()) {
        setError('Invalid or expired rollback link');
      } else {
        const data = tokenDoc.data() as RollbackToken;

        // Check if expired
        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {
          setError('This rollback link has expired');
        } else if (data.functionName !== 'command_center_permission_change') {
          setError('This rollback link is not for permission changes');
        } else {
          setRollbackData(data);
        }
      }
    } catch (err) {
      setError('Failed to load rollback data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const executeRollback = async () => {
    if (!rollbackData || !token) return;

    setExecuting(true);
    try {
      const { target_version_for_rollback, version_rolled_from } = rollbackData;

      // Extract agent name and services from the change data
      const agentName = version_rolled_from.agent;
      const previousServices = target_version_for_rollback.services || [];

      // Apply the rollback via Service Registry API
      await updateAgentPermissions(agentName, previousServices);

      // Mark token as used
      // TODO: Implement API call to update rollback token
      await apiClient.patch(`/api/rollback-tokens/${token}`, {
        used_at: new Date().toISOString(),
        used_by: user?.email || 'unknown'
      });

      // Create audit log entry for the rollback
      await createRollbackAuditEntry(agentName, version_rolled_from, target_version_for_rollback);

      setSuccess(true);
    } catch (err) {
      setError('Failed to execute rollback');
      console.error(err);
    } finally {
      setExecuting(false);
    }
  };

  const createRollbackAuditEntry = async (
    agentName: string,
    rolledFrom: any,
    rolledTo: any
  ) => {
    const auditEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: {
        email: user?.email || 'unknown',
        role: user?.role || 'unknown'
      },
      action: 'rollback.executed',
      target: {
        type: 'agent',
        id: agentName,
        displayName: agentName
      },
      changes: {
        before: rolledFrom,
        after: rolledTo,
        diff: [{
          field: 'permissions',
          operation: 'rollback',
          oldValue: rolledFrom.services,
          newValue: rolledTo.services
        }]
      },
      metadata: {
        environment: rollbackData?.env || 'production',
        rollbackTokenUsed: token
      },
      searchTags: [`agent:${agentName}`, 'action:rollback']
    };

    // TODO: Implement API call to create audit log
    await apiClient.post('/api/audit-logs', auditEntry);
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !rollbackData) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Rollback Failed
              </Typography>
              <Typography color="text.secondary" paragraph>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Rollback Successful!
              </Typography>
              <Typography color="text.secondary" paragraph>
                The permissions have been successfully rolled back to their previous state.
              </Typography>
              <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
                This rollback has been recorded in the audit log.
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/audit-log')}
              >
                View Audit Log
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!rollbackData) return null;

  const { version_rolled_from, target_version_for_rollback } = rollbackData;
  const agentName = version_rolled_from.agent;
  const currentServices = version_rolled_from.services || [];
  const previousServices = target_version_for_rollback.services || [];

  // Calculate changes
  const removed = currentServices.filter((s: string) => !previousServices.includes(s));
  const added = previousServices.filter((s: string) => !currentServices.includes(s));

  const expiresIn = Math.max(0, Math.floor(
    (new Date(rollbackData.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  ));

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 700, width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <HistoryIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            <Box>
              <Typography variant="h4">Rollback Permission Changes</Typography>
              <Typography color="text.secondary">
                Review and confirm rollback for {agentName}
              </Typography>
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            This rollback link expires in {expiresIn} days. Once used, it cannot be used again.
          </Alert>

          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current State (Will be Reverted)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              These are the current permissions that will be rolled back:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentServices.map((service: string) => (
                <Chip
                  key={service}
                  label={service}
                  size="small"
                  color={removed.includes(service) ? 'error' : 'default'}
                />
              ))}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Previous State (Will be Restored)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Permissions will be restored to this state:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {previousServices.map((service: string) => (
                <Chip
                  key={service}
                  label={service}
                  size="small"
                  color={added.includes(service) ? 'success' : 'default'}
                />
              ))}
            </Box>
          </Paper>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Summary of Changes
          </Typography>

          {added.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Services that will be RESTORED:
              </Typography>
              <List dense>
                {added.map((service: string) => (
                  <ListItem key={service}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AddIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {removed.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Services that will be REMOVED:
              </Typography>
              <List dense>
                {removed.map((service: string) => (
                  <ListItem key={service}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <RemoveIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
            This action will immediately restore the previous permissions. All changes will be logged in the audit trail.
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              disabled={executing}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={executeRollback}
              disabled={executing}
              startIcon={executing ? <CircularProgress size={20} /> : <UndoIcon />}
              sx={{ minWidth: 200 }}
            >
              {executing ? 'Executing Rollback...' : 'Confirm Rollback'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
